import {
  STORAGE_KEY, CHORE_CADENCES, WEEK_DAYS, TRANSACTION_TYPES, LEGACY_DEMO_CHORES,
  UNASSIGNED_STORE, BUDGET_ROWS, PANTRY_ITEMS, HOUSEHOLD_SUPPLIES, GROCERY_ITEMS,
  MORNING_REFLECTIONS, EVENING_REFLECTIONS
} from './constants.js';
import {
  getTodayKey, getCurrentWeekDay, getMonthKey, getPreviousDueDate, buildChores, getChoreKey
} from './helpers.js';

export const starterData = {
  crm: { projects: [] },
  chores: buildChores(),
  transactions: [
    { id: crypto.randomUUID(), label: 'Groceries', amount: 82.43, type: 'expense', date: getTodayKey(), budgetCategoryId: null },
    { id: crypto.randomUUID(), label: 'Paycheck', amount: 1850, type: 'income', date: getTodayKey(), budgetCategoryId: null },
    { id: crypto.randomUUID(), label: 'Household supplies', amount: 34.99, type: 'expense', date: getTodayKey(), budgetCategoryId: null }
  ],
  budgetCategories: BUDGET_ROWS.map(([month, category, subcategory, budgeted, actual, transactionType, expectedDue]) => ({
    id: crypto.randomUUID(), month, category, subcategory, budgeted, actual, transactionType, expectedDue
  })),
  monthlyBudget: 3200,
  weeklyBudget: 650,
  pantry: PANTRY_ITEMS.map((name) => ({ id: crypto.randomUUID(), name, quantity: 'Stock item', low: false })),
  supplies: HOUSEHOLD_SUPPLIES.map((name) => ({ id: crypto.randomUUID(), name, quantity: 'Stock item', low: false })),
  stores: [],
  groceries: GROCERY_ITEMS.map((name) => ({ id: crypto.randomUUID(), name, price: '', store: UNASSIGNED_STORE, bought: false })),
  receiptTrips: [],
  planner: {
    goals: [{ id: crypto.randomUUID(), future: 'A focused 10x future', ability: '', evidence: '', eliminate: '' }],
    reflections: {}
  },
  rewards: [
    { id: crypto.randomUUID(), title: '30 minute guilt-free break', cost: 50 },
    { id: crypto.randomUUID(), title: 'Order favorite takeout', cost: 120 }
  ],
  points: 35,
  lastResetDate: getTodayKey(),
  budgetActualsMigrated: false
};

export function applyDailyReset(data) {
  const today = getTodayKey();
  if (data.lastResetDate === today) return data;
  const chores = data.chores.map((chore) => {
    const prevDue = getPreviousDueDate(chore, today);
    if (prevDue === null) return { ...chore, doneToday: false };
    const missed = chore.lastCompletedDate !== prevDue;
    return {
      ...chore,
      doneToday: false,
      streak: missed ? 0 : chore.streak,
      lastCompletedDate: missed ? null : chore.lastCompletedDate
    };
  });
  return { ...data, chores, lastResetDate: today };
}

export function normalizeChore(item) {
  const cadence = CHORE_CADENCES.includes(item.cadence) ? item.cadence : 'Weekly';
  return {
    ...item,
    cadence,
    room: item.room || '',
    weekDay: cadence === 'Daily' ? undefined : WEEK_DAYS.includes(item.weekDay) ? item.weekDay : getCurrentWeekDay(),
    lastCompletedDate: item.lastCompletedDate || null
  };
}

function mergeChores(saved = [], defaults = []) {
  const savedWithoutDemo = saved.filter((item) => !LEGACY_DEMO_CHORES.has(item.title));
  const savedKeys = new Set(savedWithoutDemo.map((item) => getChoreKey(normalizeChore(item))));
  return [
    ...savedWithoutDemo,
    ...defaults.filter((item) => !savedKeys.has(getChoreKey(item)))
  ];
}

function mergeInventoryItems(saved = [], defaults = []) {
  const savedNames = new Set(saved.map((item) => item.name.toLowerCase()));
  return [
    ...saved.map((item) => ({ ...item, quantity: item.quantity || 'Stock item' })),
    ...defaults.filter((item) => !savedNames.has(item.name.toLowerCase()))
  ];
}

function mergeGroceryItems(saved = [], defaults = []) {
  const savedNames = new Set(saved.map((item) => item.name.toLowerCase()));
  return [
    ...saved.map((item) => ({ ...item, price: item.price || '', store: item.store || UNASSIGNED_STORE })),
    ...defaults.filter((item) => !savedNames.has(item.name.toLowerCase()))
  ];
}

function mergeBudgetCategories(saved = [], defaults = []) {
  const savedKeys = new Set(saved.map((item) => getBudgetCategoryKey(item)));
  return [
    ...saved.map((item) => ({
      ...item,
      budgeted: Number(item.budgeted) || 0,
      actual: Number(item.actual) || 0,
      transactionType: TRANSACTION_TYPES.includes(item.transactionType) ? item.transactionType : 'Fixed'
    })),
    ...defaults.filter((item) => !savedKeys.has(getBudgetCategoryKey(item)))
  ];
}

function getBudgetCategoryKey(item) {
  return `${item.month}|${item.category}|${item.subcategory}`.toLowerCase();
}

function normalizeTransactions(saved = []) {
  return saved.map((item) => ({
    ...item,
    date: item.date || null,
    budgetCategoryId: item.budgetCategoryId || null,
    type: ['income', 'expense'].includes(item.type) ? item.type : 'expense'
  }));
}

function migrateBudgetActuals(data) {
  if (data.budgetActualsMigrated) return data;
  const syntheticTransactions = data.budgetCategories
    .filter((cat) => Number(cat.actual) > 0)
    .map((cat) => ({
      id: crypto.randomUUID(),
      label: cat.subcategory,
      amount: Number(cat.actual),
      type: cat.category === 'Income' ? 'income' : 'expense',
      date: cat.expectedDue || null,
      budgetCategoryId: cat.id
    }));
  return { ...data, transactions: [...data.transactions, ...syntheticTransactions], budgetActualsMigrated: true };
}

function normalizeStores(saved = [], groceries = []) {
  const groceryStores = groceries
    .map((item) => item.store)
    .filter((store) => store && store !== UNASSIGNED_STORE);
  return [...new Set([...saved, ...groceryStores])].sort((a, b) => a.localeCompare(b));
}

export function normalizeReflection(reflection = {}) {
  return {
    morning: MORNING_REFLECTIONS.reduce((values, item) => ({ ...values, [item.key]: reflection.morning?.[item.key] || '' }), {}),
    evening: EVENING_REFLECTIONS.reduce((values, item) => ({ ...values, [item.key]: reflection.evening?.[item.key] || '' }), {})
  };
}

function normalizeTask(t) {
  return {
    id: t.id || crypto.randomUUID(),
    title: t.title || '',
    dueDate: t.dueDate || '',
    priority: t.priority || '',
    status: t.status || '',
    assignee: t.assignee || '',
    notes: t.notes || ''
  };
}

function normalizeProject(p) {
  return {
    id: p.id || crypto.randomUUID(),
    name: p.name || 'Untitled Project',
    statuses: Array.isArray(p.statuses) && p.statuses.length > 0
      ? p.statuses
      : ['Not Started', 'Deferred', 'In Progress', 'Completed', 'Waiting for input'],
    tasks: Array.isArray(p.tasks) ? p.tasks.map(normalizeTask) : []
  };
}

function normalizeCrm(crm = {}) {
  return {
    projects: Array.isArray(crm?.projects) ? crm.projects.map(normalizeProject) : []
  };
}

function normalizePlanner(planner = {}) {
  const today = getTodayKey();
  return {
    goals: Array.isArray(planner.goals) ? planner.goals : starterData.planner.goals,
    reflections: {
      ...(planner.reflections || {}),
      [today]: normalizeReflection(planner.reflections?.[today])
    }
  };
}

export function normalizeData(data) {
  const merged = {
    ...starterData,
    ...data,
    crm: normalizeCrm(data.crm),
    chores: mergeChores(data.chores, starterData.chores),
    budgetCategories: mergeBudgetCategories(data.budgetCategories, starterData.budgetCategories),
    transactions: normalizeTransactions(Array.isArray(data.transactions) ? data.transactions : []),
    pantry: mergeInventoryItems(data.pantry, starterData.pantry),
    supplies: mergeInventoryItems(data.supplies, starterData.supplies),
    groceries: mergeGroceryItems(data.groceries, starterData.groceries),
    receiptTrips: Array.isArray(data.receiptTrips) ? data.receiptTrips : starterData.receiptTrips,
    stores: normalizeStores(data.stores, data.groceries),
    planner: normalizePlanner(data.planner),
    lastResetDate: data.lastResetDate || null,
    budgetActualsMigrated: data.budgetActualsMigrated || false
  };
  return migrateBudgetActuals({
    ...merged,
    chores: (merged.chores || starterData.chores).map(normalizeChore)
  });
}

export function loadData() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return applyDailyReset(normalizeData(saved ? JSON.parse(saved) : starterData));
  } catch {
    return applyDailyReset(normalizeData(starterData));
  }
}
